import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { endpoints, qk } from '../api/endpoints';
import { useAuth, useIsOwner } from '../auth/AuthProvider';
import { HealthDot } from './common';
import type { CloneSummary } from '@api/api';
import { HEALTH_ORDER } from '../lib/status';

const WIDTH = 248;

function Brand() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 2, py: 2 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 14 }}>
        SC
      </Box>
      <Box>
        <Typography fontWeight={700} lineHeight={1.1}>
          Sharing Control
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {import.meta.env.VITE_MOCK === '1' ? 'mock data' : 'x123 fleet'}
        </Typography>
      </Box>
    </Stack>
  );
}

function CloneSearch({ clones, onPick }: { clones: CloneSummary[]; onPick: (c: CloneSummary) => void }) {
  return (
    <Autocomplete
      size="small"
      options={clones}
      getOptionLabel={(c) => `${c.name} ${c.slug} ${c.clientName} ${c.domain}`}
      filterOptions={(opts, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        return q ? opts.filter((c) => `${c.name} ${c.slug} ${c.clientName} ${c.domain}`.toLowerCase().includes(q)) : opts;
      }}
      renderOption={(props, c) => (
        <li {...props} key={c.id}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <HealthDot health={c.health} size={8} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap fontWeight={600}>
                {c.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {c.domain}
              </Typography>
            </Box>
          </Stack>
        </li>
      )}
      onChange={(_, c) => c && onPick(c)}
      value={null}
      blurOnSelect
      clearOnBlur
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Find a clone…"
          InputProps={{ ...params.InputProps, startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} /> }}
        />
      )}
    />
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const owner = useIsOwner();
  const clones = useQuery({ queryKey: qk.clones, queryFn: endpoints.clones, refetchInterval: 60_000 });
  const alerts = useQuery({ queryKey: qk.alerts(true), queryFn: () => endpoints.alerts({ open: true }), refetchInterval: 60_000 });
  const openAlerts = alerts.data?.filter((a) => !a.acknowledgedBy).length ?? 0;
  const sorted = useMemo(() => [...(clones.data ?? [])].sort((a, b) => HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health] || a.name.localeCompare(b.name)), [clones.data]);

  const item = (to: string, label: string, icon: ReactNode, end = false, badge = 0) => (
    <ListItemButton component={NavLink} to={to} end={end} onClick={onNavigate} sx={{ mb: 0.25 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Badge color="error" badgeContent={badge} max={99}>
          {icon}
        </Badge>
      </ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
    </ListItemButton>
  );

  return (
    <Stack sx={{ height: '100%' }}>
      <Brand />
      <Box sx={{ px: 1.5, pb: 1 }}>
        <CloneSearch
          clones={sorted}
          onPick={(c) => {
            navigate(`/clones/${c.id}`);
            onNavigate?.();
          }}
        />
      </Box>
      <List sx={{ px: 1 }}>
        {item('/', 'Overview', <DashboardOutlinedIcon />, true)}
        {item('/clones', 'Clones', <LayersOutlinedIcon />, true)}
      </List>
      <Box sx={{ px: 1, overflowY: 'auto', flex: '0 1 auto', maxHeight: '40vh' }}>
        {sorted.map((c) => (
          <ListItemButton key={c.id} component={NavLink} to={`/clones/${c.id}`} onClick={onNavigate} sx={{ py: 0.5, pl: 2.5 }} dense>
            <Box sx={{ mr: 1.25, display: 'flex' }}>
              <HealthDot health={c.health} size={8} />
            </Box>
            <ListItemText primary={c.name} primaryTypographyProps={{ noWrap: true, fontSize: 13 }} />
          </ListItemButton>
        ))}
        {owner && (
          <ListItemButton component={NavLink} to="/clones/new" end onClick={onNavigate} sx={{ py: 0.5, pl: 2.5, color: 'primary.main' }} dense>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            <ListItemText primary="New clone" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
          </ListItemButton>
        )}
      </Box>
      <List sx={{ px: 1 }}>
        {item('/releases', 'Releases', <NewReleasesOutlinedIcon />)}
        {item('/alerts', 'Alerts', <NotificationsOutlinedIcon />, false, openAlerts)}
        {owner && item('/settings', 'Settings', <SettingsOutlinedIcon />)}
      </List>
      <Box sx={{ flex: 1 }} />
      <Divider />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.main' }}>{user?.email[0]?.toUpperCase()}</Avatar>
        <Box component={NavLink} to="/account" onClick={onNavigate} sx={{ minWidth: 0, flex: 1, color: 'inherit', textDecoration: 'none' }} aria-label="Account">
          <Typography variant="body2" noWrap fontWeight={600}>
            {user?.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {user?.role}
          </Typography>
        </Box>
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={() => void logout()} aria-label="Sign out">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export function Layout() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const owner = useIsOwner();
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {desktop ? (
        <Drawer variant="permanent" sx={{ width: WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: WIDTH, boxSizing: 'border-box' } }}>
          <Sidebar />
        </Drawer>
      ) : (
        <>
          <AppBar position="fixed" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Toolbar variant="dense">
              <IconButton edge="start" onClick={() => setOpen(true)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
              <Typography fontWeight={700} sx={{ flex: 1, ml: 1 }}>
                Sharing Control
              </Typography>
              {owner && loc.pathname !== '/clones/new' && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => navigate('/clones/new')}>
                  New
                </Button>
              )}
            </Toolbar>
          </AppBar>
          <Drawer open={open} onClose={() => setOpen(false)} sx={{ '& .MuiDrawer-paper': { width: WIDTH } }}>
            <Sidebar onNavigate={() => setOpen(false)} />
          </Drawer>
        </>
      )}
      <Box component="main" sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, sm: 3 }, mt: desktop ? 0 : 6 }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
